'use client';

import classnames from 'classnames';
import Link from 'next/link';
import React, { CSSProperties } from 'react';

import { IconClose16 } from '../icons/ic-close';

import { BADGE_STYLE_DEFAULT_OPACITY, BadgeStyle } from './badge-styles';

export type BadgeProps = {
    className?: string;
    id?: string;
    leadingIcon?: string | React.JSX.Element;
    name: string | React.JSX.Element;
    textColor?: string;
    bgColor?: string;
    badgeStyle?: BadgeStyle;
    hoverable?: boolean;
    href?: string;
    onBadgeClick?: (badgeId: string, badgeTitle: string) => void;
    wrap?: string;
    style?: CSSProperties;
    isSelected?: boolean;
    /** Set on a badge that toggles on and off, e.g. a filter or tag chip. */
    'aria-pressed'?: boolean;
};

function applyDefaultOpacity(bgColor: string, badgeStyle: BadgeStyle): string {
    if (bgColor.includes('/')) return bgColor;
    const suffix = BADGE_STYLE_DEFAULT_OPACITY[badgeStyle];
    return suffix ? `${bgColor}${suffix}` : bgColor;
}

function Badge(props: BadgeProps): React.JSX.Element {
    const badgeStyle = props.badgeStyle || BadgeStyle.default;
    const bgColor = applyDefaultOpacity(props.bgColor || 'bg-white', badgeStyle);

    let hoverableStyle = '';
    if (props.hoverable) {
        hoverableStyle = `hover:${
            props.bgColor || 'bg-white'
        } transition-all duration-150 hover:opacity-80 active:opacity-50 cursor-pointer`;
    }

    function onClickInternal(event: React.MouseEvent, badgeId: BadgeProps['id'], badgeTitle: string) {
        if (props.onBadgeClick) {
            props.onBadgeClick(badgeId ?? '', badgeTitle);
        }
    }

    const Component = props.onBadgeClick ? 'button' : 'div';

    const badge = (
        <div
            className={classnames('flex items-center max-w-full')}
            title={typeof props.name === 'string' ? props.name : undefined}
            style={props.style}>
            <Component
                type={Component === 'button' ? 'button' : undefined}
                aria-pressed={Component === 'button' ? props['aria-pressed'] : undefined}
                className={classnames(
                    props.className,
                    badgeStyle,
                    bgColor,
                    props.textColor || 'text-gray-light',
                    hoverableStyle,
                    props.wrap || 'whitespace-nowrap',
                    'font-montserrat-semibold truncate',
                    'select-none',
                    // Row layout whenever the badge holds more than its label. This used to be tied
                    // to `isSelected` alone, so an unselected badge with a `leadingIcon` had no flex
                    // context at all and the icon wrapped onto its own line above the text.
                    {
                        'space-x-6 flex flex-row items-center': props.isSelected || !!props.leadingIcon
                    }
                )}
                onClick={(e) => {
                    onClickInternal(e, props.id, typeof props.name === 'string' ? props.name : '');
                }}>
                {props.leadingIcon}
                {props.name && <span className="truncate">{props.name}</span>}
                {props.isSelected && <IconClose16 className="opacity-20" />}
            </Component>
        </div>
    );

    return props.href && props.href.startsWith('/') ? (
        <Link href={props.href}>{badge}</Link>
    ) : props.href ? (
        <a href={props.href}>{badge}</a>
    ) : (
        badge
    );
}

export default Badge;

// Re-exported so every existing `import { BadgeStyle } from '.../Badge'` keeps resolving.
export { BadgeStyle, BADGE_STYLE_DEFAULT_OPACITY };
